import React, { useState } from 'react';
import { makeStyles } from 'react-scale/components/design-system/styles';
import { Dialog, DialogTitle } from 'design-system';
import {
  QMUpdateBenchmarksResponse,
  QMUpdateBenchmarksRequest,
} from '@scale/shared/qm/qmRequestTypes';
import { RadioGroup, Radio, Button } from 'react-scale/components/design-system';
import { BenchmarkAssignmentStatus } from '@scale/shared/defaults';
import apiFetch, { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';
import SmartLink from 'react-scale/corp/components/SmartLink/SmartLink';
import { INamedObjectTypes } from '@scale/shared/types/lookup';

const NO_CHANGE_VALUE = 'no change';

const QMBenchmarksEditAllModal: React.FC<{
  selectedIds: string[];
  showingEditAllModal: boolean;
  closeEditAllModal: () => void;
  onUpdate: () => Promise<void>;
}> = ({ selectedIds, showingEditAllModal, closeEditAllModal, onUpdate }) => {
  const classes = useStyles();
  const [status, setStatus] = useState<string>(NO_CHANGE_VALUE);
  const [isInstructiveBenchmark, setIsInstructiveBenchmark] = useState<string>(NO_CHANGE_VALUE);
  const [isAttempterBenchmark, setIsAttempterBenchmark] = useState<string>(NO_CHANGE_VALUE);

  const updateBenchmarkStatus = (status: string) => {
    setStatus(status);
  };

  const updateBenchmarkTaskerType = (isAttempter: string) => {
    setIsAttempterBenchmark(isAttempter);
  };
  const updateBenchmarkInstructive = (isInstructive: string) => {
    setIsInstructiveBenchmark(isInstructive);
  };

  const actionOnSelected = async () => {
    closeEditAllModal();
    if (!selectedIds) {
      return;
    }
    try {
      await apiFetch<QMUpdateBenchmarksResponse, QMUpdateBenchmarksRequest>(
        null,
        `corp-api/qm/benchmarks`,
        {
          method: 'PUT',
          body: {
            ids: selectedIds,
            benchmark: {
              status:
                status === NO_CHANGE_VALUE ? undefined : (status as BenchmarkAssignmentStatus),
              isInstructiveBenchmark:
                isInstructiveBenchmark === NO_CHANGE_VALUE
                  ? undefined
                  : isInstructiveBenchmark === 'true',
              isAttempterBenchmark:
                isAttempterBenchmark === NO_CHANGE_VALUE
                  ? undefined
                  : isAttempterBenchmark === 'true',
            },
          },
        },
      );
      onUpdate();
    } catch (err) {
      defaultApiFetchErrorHandler(err);
    }
  };

  return (
    <Dialog fullWidth maxWidth="lg" open={showingEditAllModal} onClose={closeEditAllModal}>
      <DialogTitle>Edit Selected Benchmarks</DialogTitle>
      <div className={classes.modalSection}>
        You are currently modifying the status of <strong>{selectedIds.length} benchmarks</strong>{' '}
        (showing {selectedIds.slice(0, 50).length} below):
        <div className={classes.examples}>
          {selectedIds.slice(0, 50).map(id => (
            <SmartLink key={id} id={id} type={INamedObjectTypes.TrainingScenario} />
          ))}
        </div>
      </div>
      <div className={classes.modalSection}>
        <h4 className={classes.modalHeader}>Status</h4>
        <RadioGroup value={status} onChange={updateBenchmarkStatus}>
          <Radio value={BenchmarkAssignmentStatus.Disabled} label="Disabled" />
          <Radio value={BenchmarkAssignmentStatus.Enabled} label="Enabled" />
          <Radio value={NO_CHANGE_VALUE} label="No change" />
        </RadioGroup>
      </div>
      <div className={classes.modalSection}>
        <h4 className={classes.modalHeader}>Benchmark Type</h4>
        <RadioGroup value={isInstructiveBenchmark} onChange={updateBenchmarkInstructive}>
          <Radio value={'true'} label="Instructive" />
          <Radio value={'false'} label="Evaluative" />
          <Radio value={NO_CHANGE_VALUE} label="No change" />
        </RadioGroup>
      </div>
      <div className={classes.modalSection}>
        <h4 className={classes.modalHeader}>Tasker Type</h4>
        <RadioGroup value={isAttempterBenchmark} onChange={updateBenchmarkTaskerType}>
          <Radio value={'true'} label="isAttempterBenchmark: true" />
          <Radio value={'false'} label="isAttempterBenchmark: false" />
          <Radio value={NO_CHANGE_VALUE} label="No change" />
        </RadioGroup>
      </div>
      <div className={classes.modalSection}>
        <Button onClick={actionOnSelected} disabled={false}>
          Update
        </Button>
        <Button onClick={closeEditAllModal}>Cancel</Button>
      </div>
    </Dialog>
  );
};

const useStyles = makeStyles({
  modalHeader: {
    marginTop: '0px',
    marginBottom: '10px',
  },
  modalSection: {
    borderBottom: '1px solid #e6e6e6',
    padding: '20px',
  },
  examples: {
    padding: '20px',
  },
});

export default QMBenchmarksEditAllModal;
